// src/pages/manager/Post.js
import React, { useCallback, useEffect, useState } from "react";
import FloatingActions from "../../ui/FloatingActions";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Container,
  Fab,
  FormGroup,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Grid,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Add,
  Delete,
  DeleteOutline,
  ExpandMore,
  Save,
  Link as LinkIcon,
  LinkOff,
  ArrowBack,
  FileCopyOutlined,
  ArrowUpward,
  ArrowDownward,
} from "@material-ui/icons";
import {
  Link as RouterLink,
  Prompt,
  useHistory,
  useLocation,
} from "react-router-dom";
import { useBmapi } from "../../utils/bmapi-context";
import { FormattedMessage, useIntl } from "react-intl";
import { posts } from "../../messages/posts";
import { common, confirm as confirmMsgs, account } from "../../messages";
import Title from "../../ui/Title";
import { getErrorMessageString } from "../../utils/errors";
import { MANAGER_ROUTES } from "../../utils/constants";
import PostPreview from "../../components/PostPreview";
import { CONTENT_APP_PATHS } from "../../utils/postUtils";
import { Alert } from "@material-ui/lab";

const useStyles = makeStyles({
  accordionDetails: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
  },
  redIcon: { color: "red" },
  summaryRow: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    gap: 8,
  },
  rightActions: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  fabWrap: { position: "relative", display: "inline-flex" },
  fabProgress: {
    position: "absolute",
    top: -6,
    right: -6,
    zIndex: 1,
  },
});

/** Iniziali per i vari tipi */
const typeMap = new Map();
typeMap.set("T", { content: { content: "", style: "body" } });
typeMap.set("I", { content: { src: "", iratio: 0 } });
typeMap.set("B", { content: { label: "", type: "P", style: "" } });
typeMap.set("MD", { content: { markdown: "" } });
typeMap.set("YT", { content: { videoId: "" } });
typeMap.set("L", { content: { type: "U", texts: [], style: "body" } });

const BLOCK_LABEL = {
  T: "Testo",
  I: "Immagine",
  MD: "Markdown",
  YT: "YouTube",
  B: "Bottone",
  L: "Lista",
};

function ContentType({ block, handleBlockValue, contentIndex, blockIndex }) {
  return (
    <FormGroup>
      {block?.type === "T" && (
        <>
          <TextField
            margin="dense"
            label="Contenuto"
            value={block?.content?.content ?? ""}
            onChange={handleBlockValue(
              contentIndex,
              blockIndex,
              "content.content"
            )}
            fullWidth
            type="text"
            multiline
            rows={4}
            variant="standard"
          />
          <TextField
            select
            label="Stile contenuto"
            value={block?.content?.style ?? "body"}
            onChange={handleBlockValue(
              contentIndex,
              blockIndex,
              "content.style"
            )}
            fullWidth
            margin="dense"
            variant="standard"
          >
            <MenuItem value="title">Titolo</MenuItem>
            <MenuItem value="subtitle">Sottotitolo</MenuItem>
            <MenuItem value="body">Body</MenuItem>
          </TextField>
        </>
      )}

      {block?.type === "I" && (
        <>
          <TextField
            margin="dense"
            label="URL"
            value={block?.content?.src ?? ""}
            onChange={handleBlockValue(contentIndex, blockIndex, "content.src")}
            fullWidth
            type="text"
            variant="standard"
          />
          <TextField
            margin="dense"
            label="Aspect ratio (iratio)"
            value={block?.content?.iratio ?? ""}
            onChange={handleBlockValue(
              contentIndex,
              blockIndex,
              "content.iratio"
            )}
            fullWidth
            type="number"
            inputProps={{ step: "0.001" }}
            variant="standard"
          />
        </>
      )}

      {block?.type === "MD" && (
        <TextField
          variant="standard"
          margin="dense"
          label="Markdown"
          value={block?.content?.markdown ?? ""}
          onChange={handleBlockValue(
            contentIndex,
            blockIndex,
            "content.markdown"
          )}
          fullWidth
          type="text"
          multiline
          rows={20}
        />
      )}

      {block?.type === "YT" && (
        <TextField
          margin="dense"
          label="Video ID"
          value={block?.content?.videoId ?? ""}
          onChange={handleBlockValue(
            contentIndex,
            blockIndex,
            "content.videoId"
          )}
          fullWidth
          type="text"
          variant="standard"
        />
      )}

      {block?.type === "B" && (
        <>
          <TextField
            margin="dense"
            label="Label"
            value={block?.content?.label ?? ""}
            onChange={handleBlockValue(
              contentIndex,
              blockIndex,
              "content.label"
            )}
            fullWidth
            type="text"
            variant="standard"
          />
          <TextField
            select
            label="Tipo"
            value={block?.content?.type ?? "P"}
            onChange={handleBlockValue(
              contentIndex,
              blockIndex,
              "content.type"
            )}
            fullWidth
            margin="dense"
            variant="standard"
          >
            <MenuItem value="P">Primary</MenuItem>
            <MenuItem value="S">Secondary</MenuItem>
            <MenuItem value="L">Link</MenuItem>
          </TextField>
          <TextField
            label="Stile"
            value={block?.content?.style ?? ""}
            onChange={handleBlockValue(
              contentIndex,
              blockIndex,
              "content.style"
            )}
            fullWidth
            margin="dense"
            variant="standard"
          />
        </>
      )}

      {block?.type === "L" && (
        <>
          <TextField
            select
            label="Tipo lista"
            value={block?.content?.type ?? "U"}
            onChange={handleBlockValue(
              contentIndex,
              blockIndex,
              "content.type"
            )}
            fullWidth
            margin="dense"
            variant="standard"
          >
            <MenuItem value="U">Puntata (unordered)</MenuItem>
            <MenuItem value="O">Numerata (ordered)</MenuItem>
          </TextField>

          <TextField
            label="Voci (una per riga)"
            value={(block?.content?.texts ?? []).join("\n")}
            onChange={(e) =>
              handleBlockValue(
                contentIndex,
                blockIndex,
                "content.texts"
              )({
                target: { value: e.target.value.split("\n") },
              })
            }
            fullWidth
            margin="dense"
            multiline
            rows={4}
            variant="standard"
          />

          <TextField
            select
            label="Stile"
            value={block?.content?.style ?? "body"}
            onChange={handleBlockValue(
              contentIndex,
              blockIndex,
              "content.style"
            )}
            fullWidth
            margin="dense"
            variant="standard"
          >
            <MenuItem value="body">Body</MenuItem>
            <MenuItem value="subtitle">Sottotitolo</MenuItem>
            <MenuItem value="title">Titolo</MenuItem>
          </TextField>
        </>
      )}
    </FormGroup>
  );
}

export default function Post() {
  const intl = useIntl();
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { bmapi, notifyError, notifySuccess } = useBmapi();

  const searchParams = new URLSearchParams(location.search);
  const postId = searchParams.get("id");
  const cloneId = searchParams.get("cloneId");

  const [values, setValues] = useState(null);
  const [expandedGroup, setExpandedGroup] = useState(false);
  const [expandedBlock, setExpandedBlock] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  // spinner visibile almeno per X ms
  const MIN_SPINNER_MS = 300;
  const withBusy = useCallback(async (fnOrPromise) => {
    const start = Date.now();
    setBusy(true);
    try {
      if (typeof fnOrPromise === "function") {
        await fnOrPromise();
      } else {
        await fnOrPromise;
      }
    } finally {
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, MIN_SPINNER_MS - elapsed);
      setTimeout(() => setBusy(false), remaining);
    }
  }, []);

  const [typeDialog, setTypeDialog] = useState({
    open: false,
    groupIndex: null,
    type: "T",
  });

  const [actionDialog, setActionDialog] = useState({
    open: false,
    groupIndex: null,
    blockIndex: null,
    type: "",
    value: "",
    errors: { type: false, value: false },
  });

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    kind: null, // 'group' | 'block'
    groupIndex: null,
    blockIndex: null,
  });

  const loadPost = useCallback(async () => {
    if (bmapi && postId) {
      // MODIFICA post esistente
      await withBusy(async () => {
        const resp = await bmapi.getPost(postId);
        setValues(resp || {});
        setDirty(false);
      });
    } else if (bmapi && cloneId) {
      // DUPLICA: carica il post sorgente ma toglie id e campi tecnici
      await withBusy(async () => {
        const resp = await bmapi.getPost(cloneId);
        if (resp) {
          const cloned = { ...resp };

          // rimuovi i campi che non vuoi copiare
          delete cloned.id;
          delete cloned.created_at;
          delete cloned.updated_at;
          // eventualmente altri:
          // delete cloned.deleted_at;
          // delete cloned.business_id;

          setValues(cloned);
        } else {
          setValues({ groups: [] });
        }
        setDirty(false);
      });
    } else {
      // NUOVO post vuoto
      setValues({ groups: [] });
      setDirty(false);
    }
  }, [bmapi, postId, cloneId, withBusy]);

  useEffect(() => {
    loadPost().catch((e) => notifyError(getErrorMessageString(e, intl)));
  }, [loadPost, notifyError, intl]);

  const handleGroupValue = useCallback(
    (index, field) => (e) => {
      const val = e.target.value;
      setDirty(true);
      setValues((v) => {
        const groups = [...(v?.groups ?? [])];
        const updated = { ...(groups[index] ?? {}), [field]: val };
        groups[index] = updated;
        return { ...(v ?? {}), groups };
      });
    },
    []
  );

  const handleBlockValue = useCallback(
    (contentIndex, blockIndex, fieldPath) => (e) => {
      const val = e.target.value;
      setDirty(true);
      setValues((v) => {
        const groups = [...(v?.groups ?? [])];
        const group = { ...(groups[contentIndex] ?? {}) };
        const blocks = [...(group.blocks ?? [])];
        let block = { ...(blocks[blockIndex] ?? {}) };

        const keys = fieldPath.split(".");
        const lastKey = keys.pop();
        let cursor = block;
        for (const k of keys) {
          const prev = cursor[k];
          cursor[k] = prev && typeof prev === "object" ? { ...prev } : {};
          cursor = cursor[k];
        }
        cursor[lastKey] = val;

        blocks[blockIndex] = block;
        group.blocks = blocks;
        groups[contentIndex] = group;
        return { ...(v ?? {}), groups };
      });
    },
    []
  );

  const handleExpandGroup = (panel) => (event, isExpanded) => {
    setExpandedGroup(isExpanded ? panel : false);
  };
  const handleExpandBlock = (panel) => (event, isExpanded) => {
    setExpandedBlock(isExpanded ? panel : false);
  };

  const handleAddGroup = () => {
    setDirty(true);
    setValues((v) => {
      const groups = [...(v?.groups ?? [])];
      const newGroup = { blocks: [], direction: "" };
      groups.push(newGroup);
      return { ...(v ?? {}), groups };
    });
  };

  // --- funzioni riordino ---

  const moveItem = (arr, from, to) => {
    const copy = [...arr];
    const item = copy[from];
    copy.splice(from, 1);
    copy.splice(to, 0, item);
    return copy;
  };

  const handleMoveGroup = (index, direction) => {
    setDirty(true);
    setValues((v) => {
      const groups = v?.groups ?? [];
      const newIndex = direction === "up" ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= groups.length) return v;
      const newGroups = moveItem(groups, index, newIndex);
      return { ...(v ?? {}), groups: newGroups };
    });
  };

  const handleMoveBlock = (groupIndex, blockIndex, direction) => {
    setDirty(true);
    setValues((v) => {
      const groups = [...(v?.groups ?? [])];
      const g = { ...(groups[groupIndex] ?? { blocks: [] }) };
      const blocks = g.blocks ?? [];
      const newIndex = direction === "up" ? blockIndex - 1 : blockIndex + 1;
      if (newIndex < 0 || newIndex >= blocks.length) return v;

      const newBlocks = moveItem(blocks, blockIndex, newIndex);
      g.blocks = newBlocks;
      groups[groupIndex] = g;
      return { ...(v ?? {}), groups };
    });
  };

  // --- fine riordino ---

  const requestDeleteGroup = (groupIndex) =>
    setConfirmDialog({
      open: true,
      kind: "group",
      groupIndex,
      blockIndex: null,
    });

  const handleRemoveGroup = (indexToRemove) => {
    withBusy(() => {
      setDirty(true);
      setValues((v) => {
        const groups = (v?.groups ?? []).filter((_, i) => i !== indexToRemove);
        return { ...(v ?? {}), groups };
      });
    });
  };

  const handleAddBlock = (groupIndex) => {
    setTypeDialog({ open: true, groupIndex, type: "T" });
  };

  const handleConfirmAddBlock = () => {
    setDirty(true);
    setValues((v) => {
      const { groupIndex, type } = typeDialog;
      const groups = [...(v?.groups ?? [])];
      const g = { ...(groups[groupIndex] ?? { blocks: [] }) };
      const payload = typeMap.get(type) || { content: {} };
      const newBlock = { type, action: null, ...payload };
      g.blocks = [...(g.blocks ?? []), newBlock];
      groups[groupIndex] = g;
      return { ...(v ?? {}), groups };
    });
    setTypeDialog({ open: false, groupIndex: null, type: "T" });
  };
  const handleCloseTypeDialog = () => {
    setTypeDialog({ open: false, groupIndex: null, type: "T" });
  };

  const requestDeleteBlock = (groupIndex, blockIndex) =>
    setConfirmDialog({ open: true, kind: "block", groupIndex, blockIndex });

  const handleRemoveBlock = (groupIndex, blockIndexToRemove) => {
    withBusy(() => {
      setDirty(true);
      setValues((v) => {
        const groups = [...(v?.groups ?? [])];
        const g = { ...(groups[groupIndex] ?? { blocks: [] }) };
        g.blocks = (g.blocks ?? []).filter((_, i) => i !== blockIndexToRemove);
        groups[groupIndex] = g;
        return { ...(v ?? {}), groups };
      });
    });
  };

  /** Azione: icona + dialog */
  const openActionDialog = (gi, bi) => {
    const act = values?.groups?.[gi]?.blocks?.[bi]?.action || null;
    setActionDialog({
      open: true,
      groupIndex: gi,
      blockIndex: bi,
      type: act?.type ?? "",
      value: act?.value ?? "",
      errors: { type: false, value: false },
    });
  };
  const closeActionDialog = () =>
    setActionDialog((s) => ({ ...s, open: false }));
  const saveActionDialog = () => {
    const { groupIndex, blockIndex, type, value } = actionDialog;
    const errors = { type: !type, value: !value };
    if (errors.type || errors.value) {
      setActionDialog((s) => ({ ...s, errors }));
      return;
    }
    setDirty(true);
    setValues((v) => {
      const groups = [...(v?.groups ?? [])];
      const g = { ...(groups[groupIndex] ?? {}) };
      const blocks = [...(g.blocks ?? [])];
      const b = { ...(blocks[blockIndex] ?? {}) };
      b.action = { type, value };
      blocks[blockIndex] = b;
      g.blocks = blocks;
      groups[groupIndex] = g;
      return { ...(v ?? {}), groups };
    });
    closeActionDialog();
  };
  const deleteAction = () => {
    withBusy(() => {
      const { groupIndex, blockIndex } = actionDialog;
      setDirty(true);
      setValues((v) => {
        const groups = [...(v?.groups ?? [])];
        const g = { ...(groups[groupIndex] ?? {}) };
        const blocks = [...(g.blocks ?? [])];
        const b = { ...(blocks[blockIndex] ?? {}) };
        b.action = null;
        blocks[blockIndex] = b;
        g.blocks = blocks;
        groups[groupIndex] = g;
        return { ...(v ?? {}), groups };
      });
      closeActionDialog();
    });
  };

  const onConfirmPost = async (event) => {
    event.preventDefault();
    await withBusy(async () => {
      try {
        let created = null;
        if (postId) {
          await bmapi.updatePost(postId, values);
        } else {
          created = await bmapi.createPost(values);
          if (created?.id) {
            setValues(created);
            history.replace(`${MANAGER_ROUTES.POST}?id=${created.id}`);
          }
        }
        notifySuccess(intl.formatMessage(account.saveConfirm));
        setDirty(false);
      } catch (e) {
        notifyError(getErrorMessageString(e, intl));
      }
    });
  };

  const pageTitle = postId ? "Modifica post" : "Crea post";
  const appPath = CONTENT_APP_PATHS.POST.replaceAll(":id", postId || "—");

  return (
    <Container maxWidth="md">
      <Prompt
        when={dirty && !busy}
        message={intl.formatMessage?.(
          confirmMsgs?.exitPrompt || {
            id: "confirm.exitPrompt",
            defaultMessage: "Hai modifiche non salvate. Uscire comunque?",
          }
        )}
      />

      <Box display="flex" alignItems="center" mb={1}>
        <IconButton
          component={RouterLink}
          to={MANAGER_ROUTES.POSTS}
          aria-label="back"
          disabled={busy}
        >
          <ArrowBack />
        </IconButton>
        <Title>
          <span>{pageTitle}</span>
        </Title>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <form onSubmit={onConfirmPost}>
            <fieldset
              disabled={busy}
              style={{ border: 0, padding: 0, margin: 0 }}
            >
              <Typography variant="body2">
                {intl.formatMessage(posts.addGroupInfo)}
              </Typography>

              <Button
                variant="text"
                startIcon={<Add />}
                onClick={handleAddGroup}
              >
                {intl.formatMessage(posts.addGroup)}
              </Button>

              {(values?.groups ?? []).map((group, contentIndex) => (
                <Accordion
                  expanded={expandedGroup === contentIndex}
                  onChange={handleExpandGroup(contentIndex)}
                  key={contentIndex}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore fontSize="small" />}
                    aria-controls={`group${contentIndex + 1}-content`}
                    id={`group${contentIndex + 1}-header`}
                  >
                    <div className={classes.summaryRow}>
                      <Typography>{`Gruppo ${contentIndex + 1}`}</Typography>
                      <div className={classes.rightActions}>
                        {/* move group up/down */}
                        <IconButton
                          size="small"
                          aria-label="move-group-up"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMoveGroup(contentIndex, "up");
                          }}
                          disabled={contentIndex === 0}
                        >
                          <ArrowUpward fontSize="small" />
                        </IconButton>

                        <IconButton
                          size="small"
                          aria-label="move-group-down"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMoveGroup(contentIndex, "down");
                          }}
                          disabled={
                            contentIndex === (values?.groups?.length ?? 0) - 1
                          }
                        >
                          <ArrowDownward fontSize="small" />
                        </IconButton>

                        <Tooltip title="Elimina gruppo">
                          <IconButton
                            className={classes.redIcon}
                            edge="end"
                            aria-label="delete-group"
                            onClick={(e) => {
                              e.stopPropagation();
                              requestDeleteGroup(contentIndex);
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </div>
                  </AccordionSummary>

                  <AccordionDetails className={classes.accordionDetails}>
                    <TextField
                      select
                      name="direction"
                      label="Direzione"
                      value={group?.direction ?? ""}
                      onChange={handleGroupValue(contentIndex, "direction")}
                      fullWidth
                      margin="dense"
                      variant="standard"
                    >
                      <MenuItem value="">
                        <em>Nessuna</em>
                      </MenuItem>
                      <MenuItem value="C">Colonna</MenuItem>
                      <MenuItem value="R">Riga</MenuItem>
                    </TextField>

                    <Box mt={2}>
                      <Button
                        variant="text"
                        size="small"
                        startIcon={<Add fontSize="small" />}
                        onClick={() => handleAddBlock(contentIndex)}
                      >
                        {intl.formatMessage(posts.addBlock)}
                      </Button>

                      {(group?.blocks ?? []).map((block, blockIndex) => {
                        const hasAction =
                          !!block?.action?.type && !!block?.action?.value;
                        return (
                          <Accordion
                            expanded={expandedBlock === blockIndex}
                            onChange={handleExpandBlock(blockIndex)}
                            key={blockIndex}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMore fontSize="small" />}
                              aria-controls={`block${blockIndex + 1}-content`}
                              id={`block${blockIndex + 1}-header`}
                            >
                              <div className={classes.summaryRow}>
                                <Typography>
                                  {BLOCK_LABEL[block?.type] || "Blocco"}
                                </Typography>

                                <div className={classes.rightActions}>
                                  {/* move block up/down */}
                                  <IconButton
                                    size="small"
                                    aria-label="move-block-up"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleMoveBlock(
                                        contentIndex,
                                        blockIndex,
                                        "up"
                                      );
                                    }}
                                    disabled={blockIndex === 0}
                                  >
                                    <ArrowUpward fontSize="small" />
                                  </IconButton>

                                  <IconButton
                                    size="small"
                                    aria-label="move-block-down"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleMoveBlock(
                                        contentIndex,
                                        blockIndex,
                                        "down"
                                      );
                                    }}
                                    disabled={
                                      blockIndex ===
                                      (group?.blocks?.length ?? 0) - 1
                                    }
                                  >
                                    <ArrowDownward fontSize="small" />
                                  </IconButton>

                                  <Button
                                    size="small"
                                    startIcon={
                                      hasAction ? (
                                        <LinkIcon fontSize="small" />
                                      ) : (
                                        <LinkOff fontSize="small" />
                                      )
                                    }
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openActionDialog(
                                        contentIndex,
                                        blockIndex
                                      );
                                    }}
                                  >
                                    {hasAction ? "Azione" : "Nessuna azione"}
                                  </Button>

                                  <Tooltip title="Elimina blocco">
                                    <IconButton
                                      className={classes.redIcon}
                                      edge="end"
                                      aria-label="delete-block"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        requestDeleteBlock(
                                          contentIndex,
                                          blockIndex
                                        );
                                      }}
                                    >
                                      <DeleteOutline fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </div>
                              </div>
                            </AccordionSummary>

                            <AccordionDetails
                              className={classes.accordionDetails}
                            >
                              {block?.type && (
                                <Box mt={1}>
                                  <ContentType
                                    block={block}
                                    handleBlockValue={handleBlockValue}
                                    contentIndex={contentIndex}
                                    blockIndex={blockIndex}
                                  />
                                </Box>
                              )}
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </fieldset>

            <FloatingActions>
              <span className={classes.fabWrap}>
                <Fab
                  variant="extended"
                  color="primary"
                  type="submit"
                  disabled={busy}
                >
                  <Save style={{ marginRight: 5 }} />
                  {intl.formatMessage(common.save)}
                </Fab>
                {busy && (
                  <CircularProgress size={48} className={classes.fabProgress} />
                )}
              </span>
            </FloatingActions>
          </form>
          <Box mt={2}>
            <Alert
              action={
                <IconButton
                  size="small"
                  aria-label="Copia"
                  onClick={() => navigator.clipboard.writeText(appPath)}
                  disabled={busy}
                >
                  <FileCopyOutlined fontSize="small" />
                </IconButton>
              }
            >
              {appPath}
            </Alert>
          </Box>
        </Grid>

        <Grid item xs={12} md={5}>
          <PostPreview post={values} />
        </Grid>
      </Grid>

      <Box mt={2}>
        <Button
          component={RouterLink}
          to={MANAGER_ROUTES.POSTS}
          startIcon={<ArrowBack />}
          disabled={busy}
        >
          <FormattedMessage id="common.back" />
        </Button>
      </Box>

      {/* Dialog: scelta tipologia blocco */}
      <Dialog
        open={typeDialog.open}
        onClose={busy ? undefined : handleCloseTypeDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Nuovo blocco</DialogTitle>
        <DialogContent>
          <TextField
            select
            label="Tipologia"
            value={typeDialog.type}
            onChange={(e) =>
              setTypeDialog((s) => ({ ...s, type: e.target.value }))
            }
            fullWidth
            margin="dense"
            variant="standard"
            disabled={busy}
          >
            <MenuItem value="T">Testo</MenuItem>
            <MenuItem value="I">Immagine</MenuItem>
            <MenuItem value="MD">Markdown</MenuItem>
            <MenuItem value="YT">YouTube</MenuItem>
            <MenuItem value="B">Bottone</MenuItem>
            <MenuItem value="L">Lista</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTypeDialog} disabled={busy}>
            Chiudi
          </Button>
          <Button
            color="primary"
            onClick={handleConfirmAddBlock}
            disabled={busy}
          >
            Aggiungi
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: azione blocco */}
      <Dialog
        open={actionDialog.open}
        onClose={busy ? undefined : closeActionDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Azione</DialogTitle>
        <DialogContent>
          <TextField
            select
            label="Tipo di azione"
            value={actionDialog.type}
            onChange={(e) => {
              const type = e.target.value;
              setActionDialog((s) => ({
                ...s,
                type,
                errors: { ...s.errors, type: false },
              }));
            }}
            fullWidth
            margin="dense"
            error={actionDialog.errors.type}
            helperText={actionDialog.errors.type ? "Obbligatorio" : ""}
            variant="standard"
            disabled={busy}
          >
            <MenuItem value="">
              <em>Seleziona…</em>
            </MenuItem>
            <MenuItem value="EP">External page</MenuItem>
            <MenuItem value="IP">Internal page</MenuItem>
            <MenuItem value="DL">Download</MenuItem>
          </TextField>

          <TextField
            margin="dense"
            label="Valore azione"
            value={actionDialog.value}
            onChange={(e) => {
              const value = e.target.value;
              setActionDialog((s) => ({
                ...s,
                value,
                errors: { ...s.errors, value: false },
              }));
            }}
            fullWidth
            type="text"
            error={actionDialog.errors.value}
            helperText={actionDialog.errors.value ? "Obbligatorio" : ""}
            variant="standard"
            disabled={busy}
          />
        </DialogContent>
        <DialogActions>
          {values?.groups?.[actionDialog.groupIndex]?.blocks?.[
            actionDialog.blockIndex
          ]?.action ? (
            <Button onClick={deleteAction} color="secondary" disabled={busy}>
              Cancella
            </Button>
          ) : null}
          <Box flexGrow={1} />
          <Button onClick={closeActionDialog} disabled={busy}>
            Chiudi
          </Button>
          <Button color="primary" onClick={saveActionDialog} disabled={busy}>
            Salva
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: conferma eliminazione */}
      <Dialog
        open={confirmDialog.open}
        onClose={
          busy
            ? undefined
            : () =>
                setConfirmDialog({
                  open: false,
                  kind: null,
                  groupIndex: null,
                  blockIndex: null,
                })
        }
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Confermi l’eliminazione?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {confirmDialog.kind === "group"
              ? "Stai per eliminare l’intero gruppo e tutti i suoi blocchi."
              : "Stai per eliminare questo blocco."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setConfirmDialog({
                open: false,
                kind: null,
                groupIndex: null,
                blockIndex: null,
              })
            }
            disabled={busy}
          >
            Annulla
          </Button>
          <Button
            color="secondary"
            onClick={() => {
              if (confirmDialog.kind === "group") {
                handleRemoveGroup(confirmDialog.groupIndex);
              } else if (confirmDialog.kind === "block") {
                handleRemoveBlock(
                  confirmDialog.groupIndex,
                  confirmDialog.blockIndex
                );
              }
              setConfirmDialog({
                open: false,
                kind: null,
                groupIndex: null,
                blockIndex: null,
              });
            }}
            disabled={busy}
          >
            Elimina
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
